import Plan from 'types/Plan';
import Typography from '@alltrails/shared/denali/components/Typography';
import { UserId } from '@alltrails/shared/types/User';
import PrimaryLink from 'components/LandingPages/PrimaryLink';
import ProFeatureComparisonTable from 'components/ProFeatureComparisonTable';
import useUser from 'hooks/useUser';
import usePlusFeatures from 'components/LandingPages/hooks/usePlusFeatures';
import Coupon from 'types/Coupon';
import { MEDIA_QUERY_SMALL_DOWN } from 'utils/constants/breakpoints';
import CurrencyCode from 'types/CurrencyCode';
import { getPriceTotal } from 'utils/PlanUtils';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import heroDesktopJpg from 'components/LandingPages/PlusLandingPageBase/images/hero-2880w.jpg';
import heroDesktopWebp from 'components/LandingPages/PlusLandingPageBase/images/hero-2880w.webp';
import heroDesktopAvif from 'components/LandingPages/PlusLandingPageBase/images/hero-2880w.avif';
import heroMobileJpg from 'components/LandingPages/PlusLandingPageBase/images/hero-750w.jpg';
import heroMobileWebp from 'components/LandingPages/PlusLandingPageBase/images/hero-750w.webp';
import heroMobileAvif from 'components/LandingPages/PlusLandingPageBase/images/hero-750w.avif';
import * as styles from './MetaLandingPage.module.scss';

// No designs, but similar to: https://www.figma.com/file/P4IkeqfAVc6FYxlyPS7Yov/F-2023_Partnerships?type=design&node-id=2449-97806&t=QuZyph2Uqqp4c2Io-0
// Disclaimer designs: https://www.figma.com/file/aXQRj7dYbtb2bNFUew23Nu/P_AllTrails%2B-Partnership-Upsells?node-id=3613%3A3401&mode=dev

type Props = {
  coupon?: Coupon;
  plan: Plan;
  userId?: UserId;
  currencyCode: CurrencyCode;
};

export default function MetaLandingPage({ plan, userId, coupon, currencyCode }: Props) {
  const user = useUser();

  const params = new URLSearchParams(__AT_DATA__.search);

  if (coupon) {
    params.set('k', coupon.code);
  }

  if (userId) {
    params.set('uid', String(userId));
  }

  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: currencyCode
  });

  const discountedPricePerYear = getPriceTotal(plan, coupon);
  const monthlyCost = discountedPricePerYear / 12;

  const search = params.toString();
  const to = `${user || userId ? '/plus/checkout' : '/signup'}${search.length > 0 ? `?${search}` : ''}`;

  const features = usePlusFeatures();

  return (
    <div className={styles.wrapper}>
      <div className={styles.hero}>
        <picture className={styles.picture}>
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileAvif} type="image/avif" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileWebp} type="image/webp" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileJpg} type="image/jpg" />
          <source srcSet={heroDesktopAvif} type="image/avif" />
          <source srcSet={heroDesktopWebp} type="image/webp" />
          <img
            alt="A trail runner hops joyfully along boulders surrounding a woodland stream. The sun-kissed water glistens, and the treetops glow vibrantly under a clear blue sky."
            src={heroDesktopJpg}
          />
        </picture>
      </div>
      <div className={styles.content}>
        <div className={styles.copy}>
          <div className={styles.headline}>Limited time only</div>
          <Typography className={styles.title} component="p">
            Save {coupon.discountPercentage}% on AllTrails+
          </Typography>
          <Typography className={styles.subhead} component="p">
            <p>
              Just <s className={styles.s}>{`${formatter.format(Number(plan.priceTotal))}`}</s>
              {` ${formatter.format(discountedPricePerYear)} for one year (that’s ${formatter.format(monthlyCost)} a month)`}
            </p>
          </Typography>
          <div className={styles.button}>
            <PrimaryLink buttonText="Save today" plan={plan} coupon={coupon} to={to} desktopVariant="accent" mobileVariant="primary" />
          </div>
        </div>
        <ProFeatureComparisonTable features={features} />
      </div>

      <Typography className={styles.disclaimer} variant="text100" component="div">
        Offer applies to purchase of a one-year subscription to AllTrails+ for first time members. This offer cannot be combined with other discounts
        or applied after an order has been placed. One redemption per person. Offer not valid on previous subscriptions to AllTrails+. Offer cannot be
        redeemed for cash or used to purchase AllTrails+ gift cards. Terms of offer are subject to change.
      </Typography>
    </div>
  );
}
